import React from "react";
import { motion } from "framer-motion";
import { Check, ArrowRight, Info } from "@phosphor-icons/react";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { useLead } from "../../context/LeadContext";
import { reveal, container } from "../../lib/motion";
import { GENERAL_TERMS, AGREEMENT } from "../../data/pricing";

export default function TermsPage() {
  const { openLead } = useLead();
  return (
    <div className="grain min-h-screen overflow-x-hidden bg-white font-sans text-ink">
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-white via-mist/60 to-white pb-14 pt-36 md:pt-44">
        <div className="pointer-events-none absolute -right-40 top-10 h-[420px] w-[420px] rounded-full bg-teal/10 blur-[130px]" />
        <div className="relative mx-auto max-w-[1000px] px-5 md:px-10">
          <motion.div variants={container} initial="hidden" animate="show">
            <motion.span variants={reveal} className="mb-4 block text-xs font-bold uppercase tracking-[0.25em] text-teal">Policies & Agreement</motion.span>
            <motion.h1 variants={reveal} className="font-display text-5xl font-black leading-[0.92] tracking-tight text-navy md:text-6xl">
              Terms & <span className="text-teal">Agreement</span>
            </motion.h1>
            <motion.p variants={reveal} className="mt-5 max-w-2xl font-sans text-lg leading-relaxed text-zinc-600">
              A plain-language overview of how we work together. The full terms are confirmed in your signed proposal or statement of work.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Refund highlight */}
      <section className="bg-white pb-6">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10">
          <div className="flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 p-6">
            <Info size={22} weight="bold" className="mt-0.5 flex-none text-gold" />
            <p className="font-sans text-sm leading-relaxed text-zinc-700">
              <strong className="font-semibold text-navy">All sales are final.</strong> Services, monthly retainers, project deposits and completed work are non-refundable. Limited exceptions may be considered at the sole discretion of Jakin Creative Design &amp; Marketing, but all transactions are otherwise considered final.
            </p>
          </div>
        </div>
      </section>

      {/* General terms */}
      <section className="bg-white py-16 md:py-20">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10">
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-8 font-display text-3xl font-black tracking-tight text-navy md:text-4xl">Good to Know</motion.h2>
          <motion.ul variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="grid gap-4 md:grid-cols-2">
            {GENERAL_TERMS.map((t, i) => (
              <motion.li key={i} variants={reveal} className="flex items-start gap-3 rounded-xl border border-zinc-200 bg-mist p-5">
                <Check size={18} weight="bold" className="mt-0.5 flex-none text-teal" />
                <span className="font-sans text-sm leading-relaxed text-zinc-700">{t}</span>
              </motion.li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* Agreement */}
      <section className="bg-mist py-16 md:py-24">
        <div className="mx-auto max-w-[1000px] px-5 md:px-10">
          <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} className="mb-8 font-display text-3xl font-black tracking-tight text-navy md:text-4xl">Working Together</motion.h2>
          <motion.div variants={container} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} className="space-y-5">
            {AGREEMENT.map((a) => (
              <motion.div key={a.h} variants={reveal} className="rounded-2xl border border-zinc-200 bg-white p-6 md:p-7">
                <h3 className="font-display text-lg font-bold tracking-tight text-navy">{a.h}</h3>
                <p className="mt-2 font-sans text-sm leading-relaxed text-zinc-600">{a.p}</p>
              </motion.div>
            ))}
          </motion.div>
          <div className="mt-8 flex items-start gap-3 rounded-2xl border border-gold/40 bg-gold/10 p-6">
            <Info size={22} weight="bold" className="mt-0.5 flex-none text-gold" />
            <p className="font-sans text-sm leading-relaxed text-zinc-700">This overview is provided for clarity and is not legal advice. Please have a qualified Florida attorney review the agreement before it is used as your business's final legal contract.</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden bg-navy py-20 md:py-28">
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="h-[300px] w-[300px] rounded-full bg-teal/20 blur-[120px] md:h-[460px] md:w-[460px]" />
        </div>
        <div className="relative mx-auto max-w-3xl px-5 text-center md:px-10">
          <h2 className="font-display text-3xl font-black leading-[0.95] tracking-tight text-white md:text-5xl">Questions about working together?</h2>
          <p className="mx-auto mt-4 max-w-xl font-sans text-base leading-relaxed text-white/70">Book a free consultation and we'll walk you through everything.</p>
          <button onClick={() => openLead("consultation")} className="group mx-auto mt-8 flex items-center gap-3 bg-teal px-8 py-4 text-sm font-bold uppercase tracking-wider text-white transition-colors hover:bg-white hover:text-navy" data-testid="terms-cta-book">
            Book a Consultation <ArrowRight weight="bold" className="transition-transform group-hover:translate-x-1" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
